import { Controller, FieldValues, useFormState } from 'react-hook-form';
import { BaseFormFieldProps } from './types';
import StatusSelector, { StatusOption } from '../selectors/StatusSelector';

export interface FormStatusSelectorProps<TFieldValues extends FieldValues = FieldValues>
  extends Omit<BaseFormFieldProps<TFieldValues>, 'label' | 'placeholder'> {
  options?: StatusOption[];
  placeholder?: string;
  label?: string;
  variant?: 'flat' | 'bordered' | 'faded' | 'underlined';
  showAllOption?: boolean;
  showEmptyOption?: boolean;
}

const FormStatusSelector = <TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  isRequired = false,
  showErrorOnTouched = true,
  options,
  placeholder,
  label,
  showAllOption = false,
  showEmptyOption,
  ...rest
}: FormStatusSelectorProps<TFieldValues>) => {
  const { touchedFields, errors, isSubmitted } = useFormState({ control });
  const fieldError = errors[name]?.message as string | undefined;
  const isTouched = touchedFields[name];
  const showError = showErrorOnTouched ? (isTouched || isSubmitted) && !!fieldError : !!fieldError;

  // Default showEmptyOption to the opposite of showAllOption for Form versions
  const defaultShowEmptyOption = showEmptyOption !== undefined ? showEmptyOption : !showAllOption;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <StatusSelector
          value={field.value}
          onChange={field.onChange}
          options={options}
          isRequired={isRequired}
          isInvalid={showError}
          errorMessage={showError ? fieldError : undefined}
          placeholder={placeholder}
          label={label}
          showAllOption={showAllOption}
          showEmptyOption={defaultShowEmptyOption}
          {...rest}
        />
      )}
    />
  );
};

FormStatusSelector.displayName = 'FormStatusSelector';

export default FormStatusSelector;
